// ─────────────────────────────────────────────────────────────────────────
// useDeliveryCamera.ts
// Ports openDeliveryCamera(), stopDeliveryCameraStream(), captureDeliveryPhoto(),
// previewDeliveryPhoto(event), and retakeDeliveryPhoto() from the original
// inline script. Falls back to the device's native camera via
// <input type="file" accept="image/*" capture="environment"> when
// getUserMedia is unsupported or denied — same as the original.
// ─────────────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useRef, useState } from 'react';

const MAX_PHOTO_BYTES = 10 * 1024 * 1024; // 10MB, matches the original's check

/** Which physical camera is currently selected. 'user' = front/selfie camera, 'environment' = rear camera. */
export type CameraFacingMode = 'user' | 'environment';

export interface UseDeliveryCameraResult {
  videoRef: React.RefObject<HTMLVideoElement>;
  fileInputRef: React.RefObject<HTMLInputElement>;
  /** True while the live getUserMedia stream is active (show the shutter button). */
  isStreaming: boolean;
  /** The captured/selected photo as a data URI, or null if none yet. */
  photoDataURI: string | null;
  /** Which camera is currently active/will be opened next ('user' = front, 'environment' = rear). */
  facingMode: CameraFacingMode;
  /** Starts the in-page camera, or falls back to the OS camera picker. Call from the upload-area tap handler. */
  openCamera: () => Promise<void>;
  /** Captures the current video frame into photoDataURI and stops the stream. */
  capturePhoto: () => void;
  /** Switches between front (user) and rear (environment) camera while streaming, restarting getUserMedia with the new facingMode. */
  switchCamera: () => Promise<void>;
  /** Handles a file selected via the fallback <input type="file">. */
  handleFileSelected: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /** Clears the captured photo and stops any active stream, returning to the initial state. */
  retake: () => void;
  /** Error message (e.g. "photo too large"), or null. Caller is responsible for localizing/displaying it. */
  error: 'too_large' | null;
  clearError: () => void;
}

export function useDeliveryCamera(): UseDeliveryCameraResult {
  const videoRef = useRef<HTMLVideoElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const [isStreaming, setIsStreaming] = useState(false);
  const [photoDataURI, setPhotoDataURI] = useState<string | null>(null);
  const [error, setError] = useState<'too_large' | null>(null);
  // Defaults to the front ('user') camera; switchCamera() flips this and
  // restarts the stream with the new facingMode.
  const [facingMode, setFacingMode] = useState<CameraFacingMode>('user');

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setIsStreaming(false);
  }, []);

  /** Starts getUserMedia with the given facingMode. Returns true on success. */
  const startStream = useCallback(async (mode: CameraFacingMode): Promise<boolean> => {
    if (!navigator.mediaDevices?.getUserMedia) return false;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: mode },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
      setIsStreaming(true);
      return true;
    } catch {
      return false;
    }
  }, []);

  const openCamera = useCallback(async () => {
    // Don't reopen if a photo is already captured — caller must retake() first, same as the original.
    if (photoDataURI) return;

    const started = await startStream(facingMode);
    if (!started) {
      // getUserMedia unsupported, permission denied, or camera unavailable —
      // fall back to the device's native camera UI.
      fileInputRef.current?.click();
    }
  }, [photoDataURI, facingMode, startStream]);

  /** Toggles between front ('user') and rear ('environment') camera while streaming. */
  const switchCamera = useCallback(async () => {
    const nextMode: CameraFacingMode = facingMode === 'user' ? 'environment' : 'user';
    stopStream();
    setFacingMode(nextMode);
    await startStream(nextMode);
  }, [facingMode, stopStream, startStream]);

  const capturePhoto = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    setPhotoDataURI(canvas.toDataURL('image/jpeg', 0.92));
    stopStream();
  }, [stopStream]);

  const handleFileSelected = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_PHOTO_BYTES) {
      setError('too_large');
      return;
    }
    setError(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      setPhotoDataURI(typeof e.target?.result === 'string' ? e.target.result : null);
    };
    reader.readAsDataURL(file);
  }, []);

  const retake = useCallback(() => {
    setPhotoDataURI(null);
    setError(null);
    stopStream();
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [stopStream]);

  const clearError = useCallback(() => setError(null), []);

  // Stop any active stream if the component unmounts mid-stream (e.g. modal force-closed).
  useEffect(() => stopStream, [stopStream]);

  // Re-attach the live MediaStream to the <video> element whenever it changes
  // identity (e.g. the caller renders a different <video> tag once isStreaming
  // flips to true, such as swapping to a full-screen camera layout). Without
  // this, the stream can be assigned to a video element that gets unmounted
  // before paint, leaving the newly-mounted one with no srcObject — the
  // classic "black camera" symptom.
  useEffect(() => {
    if (isStreaming && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.play().catch(() => {});
    }
  }, [isStreaming]);

  return {
    videoRef,
    fileInputRef,
    isStreaming,
    photoDataURI,
    facingMode,
    openCamera,
    capturePhoto,
    switchCamera,
    handleFileSelected,
    retake,
    error,
    clearError,
  };
}
